/**
 * NewProjectModal — Iter 38.5 plan-admin usability correction.
 *
 * Compact, tactical, restrained. Creates a new project via
 * POST /api/projects. On success, navigates to /project/{id} so the user
 * can immediately upload sheets — the directive's "How do I add plan
 * sheets?" needs to be answerable in 10 seconds from the moment of project
 * creation.
 *
 * Not a wizard. Not a multi-step. Single form, four required fields.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Loader2 } from "lucide-react";

// Iter 141 — full heavy-civil discipline catalog (matches backend
// `DISCIPLINE_LABELS`). Each entry has a snake_case `value` (the wire
// key) + a professional `label` for display.
const DISCIPLINE_OPTIONS = [
  { value: "cover_sheet",         label: "Cover Sheet" },
  { value: "general",             label: "General" },
  { value: "survey",              label: "Survey" },
  { value: "existing_conditions", label: "Existing Conditions" },
  { value: "demolition",          label: "Demolition" },
  { value: "erosion_control",     label: "Erosion Control" },
  { value: "mot",                 label: "Maintenance of Traffic" },
  { value: "earthwork",           label: "Earthwork" },
  { value: "drainage",            label: "Drainage" },
  { value: "utility",             label: "Utilities" },
  { value: "water",               label: "Water" },
  { value: "sanitary_sewer",      label: "Sanitary Sewer" },
  { value: "storm_sewer",         label: "Storm Sewer" },
  { value: "force_main",          label: "Force Main" },
  { value: "roadway",             label: "Roadway" },
  { value: "grading",             label: "Grading" },
  { value: "paving",              label: "Paving" },
  { value: "signing_striping",    label: "Signing & Pavement Marking" },
  { value: "traffic_signal",      label: "Traffic Signal" },
  { value: "lighting",            label: "Lighting" },
  { value: "electrical",          label: "Electrical" },
  { value: "structures",          label: "Structures" },
  { value: "landscape",           label: "Landscape" },
  { value: "irrigation",          label: "Irrigation" },
  { value: "cross_sections",      label: "Cross Sections" },
  { value: "profiles",            label: "Profiles" },
  { value: "details",             label: "Details" },
  { value: "geotechnical",        label: "Geotechnical" },
  { value: "environmental",       label: "Environmental" },
  { value: "other",               label: "Other" },
];


export default function NewProjectModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  // Iter 39 — auto-generated default. The directive: 'Auto-generated by
  // default. Editable by authorized users. Do not require manual numbering.'
  // Pattern: PRJ-{YYYY}-{HHMMSS} so it's chronologically meaningful AND
  // collision-free without round-tripping to the server.
  const autoNumber = React.useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `PRJ-${yyyy}-${hh}${mm}${ss}`;
  }, []);
  const [number, setNumber] = useState(autoNumber);
  const [numberEdited, setNumberEdited] = useState(false);
  const [client, setClient] = useState("");
  const [location, setLocation] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const valid = name.trim() && number.trim() && client.trim() && location.trim();

  const toggleDiscipline = (d) => {
    setDisciplines((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true); setError(null);
    try {
      const { data } = await api.post("/projects", {
        name: name.trim(),
        number: number.trim(),
        client: client.trim(),
        location: location.trim(),
        disciplines,
      });
      if (onCreated) onCreated(data);
      // Take the admin straight to the project so they can upload sheets.
      navigate(`/project/${data.id}`);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create project");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="new-project-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl rounded-xl border border-[var(--fop-border-2)] bg-[var(--fop-surface)] shadow-2xl my-auto max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-7 py-5 border-b border-[var(--fop-border)]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-steel)]">
              Plan Room
            </div>
            <h2 className="font-display text-2xl text-[var(--fop-text)] mt-1">Create New Project</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" aria-label="Close" data-testid="new-project-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-7 py-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Project name" testid="np-name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. SR-22 Road Widening"
                data-testid="new-project-name"
                autoFocus
              />
            </Field>
            <Field label="Number" testid="np-number" hint="auto-generated">
              <input
                value={number}
                onChange={(e) => { setNumber(e.target.value.toUpperCase()); setNumberEdited(true); }}
                placeholder="PRJ-2026-…"
                data-testid="new-project-number"
                data-autogenerated={numberEdited ? "0" : "1"}
                title={numberEdited ? "" : "Auto-generated. Edit if you have a project number from your system."}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Client" testid="np-client">
              <input
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="City of Brookhaven Public Works"
                data-testid="new-project-client"
              />
            </Field>
            <Field label="Location" testid="np-location">
              <input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="5 city blocks — Elm to Magnolia"
                data-testid="new-project-location"
              />
            </Field>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-2">
              Disciplines <span className="text-[var(--fop-text-4)]">(optional)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {DISCIPLINE_OPTIONS.map((d) => {
                const on = disciplines.includes(d.value);
                return (
                  <button
                    type="button"
                    key={d.value}
                    onClick={() => toggleDiscipline(d.value)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${on ? "border-[var(--fop-primary)] text-[var(--fop-primary)] bg-[var(--fop-primary-soft)]" : "border-[var(--fop-border-2)] text-[var(--fop-text-3)] hover:text-[var(--fop-text-2)] hover:border-[var(--fop-border-hi)]"}`}
                    data-testid={`new-project-discipline-${d.value}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
          {error && (
            <div className="text-sm text-red-400 font-mono" data-testid="new-project-error">{error}</div>
          )}
        </div>

        <div className="px-7 py-4 border-t border-[var(--fop-border)] flex flex-wrap items-center justify-end gap-3 bg-[var(--fop-surface-2)] rounded-b-xl">
          {!valid && (
            <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] mr-auto" data-testid="new-project-missing">
              Fill all *required fields to continue
            </span>
          )}
          <button type="button" onClick={onClose} className="btn btn-ghost text-sm">Cancel</button>
          <button
            type="submit"
            disabled={!valid || submitting}
            className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="new-project-submit"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Create project
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, children, testid, className = "", required = true, hint }) {
  return (
    <label className={`block ${className}`} data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1 flex items-center gap-1">
        {label}
        {required && <span className="text-[var(--fop-primary)]" title="Required" aria-label="required">*</span>}
        {hint && <span className="text-[var(--fop-text-4)] normal-case tracking-normal ml-1">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
